import React from 'react';

import { Button, Stack, Text } from '@mantine/core';
import { ContextModalProps } from '@mantine/modals';
import { z } from 'zod';

import { Form } from 'components/forms/Form';
import { FormSubmit } from 'components/forms/FormSubmit';
import { MultilineTextField } from 'components/forms/fields/MultilineTextField';
import { ModalTitle } from 'components/modals/ModalTitle';
import { InternalTrackingService } from 'services/InternalTrackingService';
import { LocalNotificationService } from 'services/LocalNotificationService';

type Values = {
    feedback: string;
};

const schema = z.object({
    feedback: z.string().trim().min(1, 'Please share a little feedback'),
});

const PricingExitFeedbackModal = ({ context, id }: ContextModalProps) => {
    const submit = async (values: Values) => {
        InternalTrackingService.track(
            'pricing.page.exit-feedback.submit',
            { feedback: values.feedback },
            { notifySlack: true, includeAnonymous: true },
        );

        LocalNotificationService.showSuccess({
            message: 'Thank you for your feedback!',
        });

        context.closeModal(id);
    };

    const dismiss = () => {
        InternalTrackingService.track('pricing.page.exit-feedback.dismiss');

        context.closeModal(id);
    };

    return (
        <Stack gap="xs" p="sm">
            <ModalTitle>Before you go 👋</ModalTitle>
            <Text ta="center" c="dimmed" fz="sm">
                We noticed you checked out our pricing. We&apos;d love to hear what held you back - your feedback helps
                us improve DCIDE.
            </Text>
            <Form<Values> defaultValues={{ feedback: '' }} zodSchema={schema} onSubmit={submit}>
                <Stack gap="xs">
                    <MultilineTextField
                        name="feedback"
                        placeholder="What stopped you from upgrading today?"
                        minRows={3}
                        autosize
                        required
                        data-autofocus
                    />
                    <FormSubmit>Send feedback</FormSubmit>
                    <Button variant="subtle" color="gray" size="xs" onClick={dismiss}>
                        No thanks
                    </Button>
                </Stack>
            </Form>
        </Stack>
    );
};

export { PricingExitFeedbackModal };
